import React, { useEffect, useMemo, useRef, useState } from 'react';
import CarouselComponent from '../../../Misc/CarouselComponent/CarouselComponent';
import styles from './styles.module.scss';
import axios from 'axios';
import { useSession } from 'next-auth/react';
import { BarLoader, PuffLoader, PulseLoader, RiseLoader } from 'react-spinners';

import errorMsg from '../../../Functions/errorMsg';
import successMsg from '../../../Functions/successMsg';

import { faAd, faBoltLightning, faBookmark, faCalendarDays, faCheck, faClock, faCoins, faEye, faFile, faFileArrowUp, faFlag, faHandHoldingDollar, faHardHat, faHeart, faHome, faLink, faLocationDot, faMessage, faMoneyBill, faMoneyBillTransfer, faPaperPlane, faPenToSquare, faPencil, faPeopleGroup, faPhone, faShare, faStar, faUpload, faWalking, faXmark, faXmarkCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import openAdManager from '../../../Functions/openAdManager';

import Image from 'next/image';
import { AnimatePresence, useInView } from 'framer-motion';
import { useRouter } from 'next/router';

import MapWithLocation from '../../../Misc/MapWithLocation/MapWithLocation';

import { motion } from 'framer-motion';
import OnlineProfile from '../../UserProfile/OnlineProfile/OnlineProfile';
import { useSocket } from '../../../Context/socket';
import MapWithRadius from '../../../Misc/MapWithRadius/MapWithRadius';

export default function JobListing({ listingID, closeListing }) {

    const { data: session, status } = useSession();
    const { socket, socketConnected } = useSocket();
    const router = useRouter();

    const [initialLoading, setInitialLoading] = useState(null);

    const [data, setData] = useState(null);
    const [posterProfile, setPosterProfile] = useState(null);
    const [reviewData, setReviewData] = useState(null);

    const [reported, setReported] = useState(false);

    const [hasApplication, setHasApplication] = useState(false);

    useEffect(() => {
        if(router === null || router === undefined) return;
        if(router.query === null || router.query === undefined) return;
        if(!router.query.openConversationID) return;
        closeListing(null);
      }, [router])

    useEffect(() => {
        if(initialLoading !== null) return;
        if(data !== null) return;
        if(status === 'loading') return;

        const requestingUser = status === 'unauthenticated' ? 'nouser' : session.user.token.username;

        async function getListingData() {
            setInitialLoading(true);

            const result = await axios.post(`${window.location.origin}/api/jobs/general/getListing`, {
                listingID: listingID,
                editData: false,
                requestingUser: requestingUser
            }).catch((error) => {
                errorMsg(error?.response?.data?.status || error.toString())
                closeListing(null);
                setInitialLoading(false);
            })

            if(result && result.status === 200) {
                const listingData = result.data;

                setData(listingData.listing);
                setPosterProfile(listingData.profile);
                setReviewData(returnRating(listingData.listing.reviews));

                if(requestingUser !== 'nouser') {
                    setReported(listingData.listing.reports.some((report) => report.reportingUser === requestingUser))
                    setHasApplication(listingData.listing.applicants.some((app) => app.user === requestingUser))
                }
            }
            setInitialLoading(false);
        }

        getListingData();

    }, [session, status])

    function isLoading() {
        if(status === 'loading') return true;
        if(initialLoading === true || initialLoading === null) return true;
        if(data === null) return true;
        if(posterProfile === null) return true;
        if(reviewData === null) return true;
        return false;
    }

    function authenticationIsOwnerCheck() {
        if(!session) return false;
        if(status !== 'authenticated') return false;
        if(session.user.token.username === data.owner) return false;
        return true;
      }

    const memoizedOptionBar = useMemo(() => <OptionBar listing={data} session={session} closeListing={closeListing} authenticationIsOwnerCheck={authenticationIsOwnerCheck} handleCreateReport={() => setCreateReport({listingID: data._id, reportingUser: data.owner})} reported={reported} />, [data, session, closeListing, reported])

    const [profileLoaded, setProfileLoaded] = useState(false);
    const [viewUserProfile, openUserProfile] = useState(false);

    const [createReport, setCreateReport] = useState(null);

    const [loadingHasConvo, setLoadingHasConvo] = useState(false);
    const [hasConvo, setHasConvo] = useState(null);

    useEffect(() => {
        if(loadingHasConvo || hasConvo !== null) return;
        if(!session) return;
        if(status === 'loading') return;
        if(!data) return;
        if(!posterProfile) return;

        const controller = new AbortController();

        const profileUsername = posterProfile.username;

        async function getUserProfile() {
            setLoadingHasConvo(true);
            const result = await axios.post(`${window.location.origin}/api/messaging/hasActiveConvo`, {
                signal: controller.signal,
                requestingUserID: session.user.token.id,
                otherUser: profileUsername,
                convoType: 'Jobs',
                listingID: data._id
            }).catch((error) => {
                errorMsg(error?.response?.data?.status || error.toString());
            })

            if(result && result.status === 200) {
                const data = result.data;
                setHasConvo({ hasConvo: data.hasConvo, convoID: data.convoID });
            }

            setLoadingHasConvo(false);
        }

        getUserProfile();

        return () => {
            controller.abort();
        }

    }, [status, posterProfile, data, posterProfile])

    const quickMessageTextRef = useRef(null);

    async function handleQuickMessageSend() {
        if(loadingHasConvo) return;

        if(!posterProfile) return errorMsg("There was an error sending your message, please try again later.");

        if(!session) return errorMsg("Please login to message this user.");
        if(status === 'loading') return;
        if(status === 'unauthenticated') return errorMsg("Please login to message this user.");

        if(!socket) return errorMsg("There was an error sending your message, please try again later.");
        if(!socketConnected) return errorMsg("There was an error sending your message, please try again later.");

        if(!quickMessageTextRef || !quickMessageTextRef.current) return;

        const messageText = quickMessageTextRef.current.value;

        if(messageText.length === 0) return errorMsg("Please enter a message before submitting.");
        if(messageText.length > 1000) return errorMsg("Your message is too long, please submit below 1000 characters.");

        const toUser = posterProfile.username;

        const listingID = data._id;

        const senderID = session.user.token.id;
        const sender = session.user.token.username;

        const basicConvoData = {
            type: 'Jobs',
            listingID: listingID,
            receiver: toUser,
            sender: sender,
            creationDate: new Date().toISOString()
        }

        setLoadingHasConvo(true);

        const result = await axios.post(`${window.location.origin}/api/messaging/create`, {
            conversationData: basicConvoData,
            messageText: messageText,
            senderID: senderID,
        }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString())
        });

        if(result && result.status === 200) {
            const newConvoData = result.data;

            const hasNewConvoData = {
                hasConvo: true,
                convoID: newConvoData._id
            }

            setHasConvo(hasNewConvoData)

            socket.emit('createNewConversation', newConvoData)

            successMsg("Your message was sent.");

        }

        setLoadingHasConvo(false);

    }

    function handleOpenCurrentConversation() {
        if(!hasConvo) return errorMsg("Could not find this conversation, please try again later");
        if(hasConvo.hasConvo === false) return errorMsg("You do not have a conversation with this user.");

        const { query } = router;

        const updatedQuery = { ...query, openConversationID: hasConvo.convoID };

        router.push({ pathname: router.pathname, query: updatedQuery });
    }

    const {displayTime, displayTimeType} = createdTimeStamp(data?.creationDate);


    // Easy Apply Methods & Functionality ====>
    // =======================================>

    const [applyPending, setApplyPending] = useState(false);

    const [resumeData, setResumeData] = useState(null);
    const nameRef = useRef(null);
    const phoneRef = useRef(null);
    const emailRef = useRef(null);
    const [phoneInput, setPhoneInput] = useState(null);

    async function handleEasyApply() {
        if(applyPending) return;

        if(nameRef.current.value === '') return errorMsg("Please enter your first & last name");
        if(phoneInput === '' || phoneInput === null) return errorMsg("Please enter your phone number");
        if(emailRef.current.value === '') return errorMsg("Please enter your email address");
        if(resumeData === null) return errorMsg("Please upload your resume");

        setApplyPending(true);

        const result = await axios.post(`${window.location.origin}/api/jobs/helpers/easyApply`, {
            jobID: listingID,
            requestingUserID: session.user.token.id,
            name: nameRef.current.value,
            phone: phoneInput,
            email: emailRef.current.value,
            resume: resumeData
        }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString());
        })

        if(result && result.status === 200) {

            setHasApplication(true);
            successMsg("Your applications was submitted.");
        }

        setApplyPending(false);

    }

    const handlePhoneInput = (event) => {
        const input = event.target.value.replace(/\D/g, ''); // Remove non-numeric characters
        let formattedPhoneNumber = '';
        
        if (input.length >= 4 && input.length <= 7) {
          formattedPhoneNumber = `(${input.slice(0, 3)}) ${input.slice(3)}`;
        } else if (input.length > 7) {
          formattedPhoneNumber = `(${input.slice(0, 3)}) ${input.slice(3, 6)}-${input.slice(6, 10)}`;
        } else {
          formattedPhoneNumber = input;
        }
        
        setPhoneInput(formattedPhoneNumber);
      };

    // Uploading Resume Methods

    const [uploadingResume, setUploadingResume] = useState(false);

    const handleUploadResume = async (e) => {
        e.preventDefault();
    
        if (uploadingResume) return;
        if (resumeData) return;

        const getFile = e.target.files[0] || e.dataTransfer.files[0];

        if(getFile === null || getFile === undefined) return errorMsg("There was an error uploading your resume, please try again later.");
    
        setUploadingResume(true);
    
        async function getBase64(file) {
          return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = () => {
              resolve(reader.result)
            }
            reader.onerror = reject
          })
        }
    
        const pdfBase64 = await getBase64(getFile);

        setResumeData(pdfBase64);
        setUploadingResume(false);

    };

    function removeLogo() {
        if(!resumeData) return;
        setResumeData(null);
    }

  return (
    <div id={styles.wrapper}>
        <AnimatePresence>
            { !reported && createReport && <CreateReportBox listingID={createReport.listingID} reportingUser={createReport.reportingUser} closeReport={() => setCreateReport(null)} successfulReport={() => setReported(true)} /> }
        </AnimatePresence>

        { viewUserProfile && <OnlineProfile profileUsername={posterProfile.username} closeProfile={() => openUserProfile(false)} /> }
        { isLoading() === true && <PulseLoader size={30} color='white' /> }
        { isLoading() === false && 
        <div id={styles.listing}>

            {memoizedOptionBar}

            <section id={styles.carouselContainer}>
                { data.remotePosition && <MapWithRadius zipCode={data.zip} /> }
                { !data.remotePosition && <MapWithLocation address={data.propertyAddress} city={data.propertyCity} zipCode={data.zip} /> }


                <div id={styles.listingInfoDots}>
                    <div style={{display: data.views.length === 0 ? 'none' : 'flex'}}>
                      <FontAwesomeIcon icon={faEye} id={styles.icon} />
                      <h1>{data.views.length}</h1>
                    </div>
                    <div style={{display: data.saves.length === 0 ? 'none' : 'flex'}}>
                      <FontAwesomeIcon icon={faBookmark} id={styles.icon} />
                      <h1>{data.saves.length}</h1>
                    </div>
                  </div>
            </section>

            <div id={styles.infoContainer}>

                <div id={styles.info}>
                    <h1>{data.positionTitle}</h1>
                    <h2>{data.positionType}</h2>
                    <h3>Listing posted {displayTime} {displayTimeType}</h3>
                    <hr />
                    <h1 style={{display: data.desc === '' ? 'none' : 'flex'}}>Details</h1>
                    <p style={{display: data.desc === '' ? 'none' : 'flex'}}>{data.desc}</p>
                    <hr style={{display: data.desc === '' ? 'none' : 'flex'}} />
                    <span id={styles.detailBoxes}>

                        <div className={styles.colorPurple} style={{display: data.applyPreferences.includes('Walk-Ins') ? 'flex' : 'none'}}>
                            <FontAwesomeIcon icon={faWalking} id={styles.icon} />
                            <h1>Walk-In Applications</h1>
                        </div>

                        <div className={styles.colorGreen}>
                            <FontAwesomeIcon icon={faCoins} id={styles.icon} />
                            <h1>Pay Rate</h1>
                            <h2>${data.rate.type === 'Hourly' ? `${parseFloat(data.rate.rate).toLocaleString()} / hr` : `${parseFloat(data.rate.rate).toLocaleString()} / year`}</h2>
                        </div>

                        <div className={styles.colorRed} style={{display: data.remotePosition ? 'flex' : 'none'}}>
                            <FontAwesomeIcon icon={faHome} id={styles.icon} />
                            <h1>Remote Position</h1>
                            <h2>ZIP {data.zip}</h2>
                        </div>

                        <div className={styles.colorRed} style={{display: !data.remotePosition ? 'flex' : 'none'}}>
                            <FontAwesomeIcon icon={faHome} id={styles.icon} />
                            <h1>Employer Located</h1>
                            <h2>{data.propertyCity}</h2>
                        </div>

                        <div className={styles.colorRed}>
                            <FontAwesomeIcon icon={faMoneyBillTransfer} id={styles.icon} />
                            <h1>{data.paymentFrequencies.length === 0 ? 'Payment Duration' : 'Payment Durations'}</h1>
                            <h2>{data.paymentFrequencies.map((paymentFrequency) => paymentFrequency).join(', ')}</h2>
                        </div>

                        <div className={styles.colorOrange}>
                            <FontAwesomeIcon icon={faHandHoldingDollar} id={styles.icon} />
                            <h1>{data.payoutTypes.length === 0 ? 'Payout Type' : 'Payout Types'}</h1>
                            <h2>{data.payoutTypes.map((payoutType) => payoutType).join(', ')}</h2>
                        </div>

                        <div className={styles.colorYellow} style={{display: data.multipleApplicants ? 'flex' : 'none'}}>
                            <FontAwesomeIcon icon={faPeopleGroup} id={styles.icon} />
                            <h1>Multiple Candidates</h1>
                        </div>

                        <div className={styles.colorYellow} style={{display: data.urgentlyHiring ? 'flex' : 'none'}}>
                            <FontAwesomeIcon icon={faHardHat} id={styles.icon} />
                            <h1>Urgently Hiring</h1>
                        </div>

                        <div >
                            <FontAwesomeIcon icon={faCalendarDays} id={styles.icon} />
                            <h1>{data.employmentTypes.length === 0 ? 'Schedule' : 'Schedules'}</h1>
                            <h2>{data.employmentTypes.map((employmentType) => employmentType).join(', ')}</h2>
                        </div>

                        <div>
                            <FontAwesomeIcon icon={faClock} id={styles.icon} />
                            <h1>{data.shiftTypes.length === 0 ? 'Shift' : 'Shifts'}</h1>
                            <h2>{data.shiftTypes.map((shiftType) => shiftType).join(', ')}</h2>
                        </div>



                    </span>

                    <h1 style={{ marginTop: '18px'}}>Schedule Details</h1>
                    <p>{data.scheduleDetails}</p>
                    <hr />
                </div>

                <div id={styles.employerDetails}>
                    <h1>Employer Details</h1>

                    <section>
                        <div>
                            <Image sizes='auto auto' fill objectFit='cover' src={data.businessLogo} alt='' />
                        </div>
                        <h1>{data.businessTitle}</h1>
                    </section>

                </div>

                <div style={{marginBottom: '12px'}} id={styles.rentalPropertyLocation} onClick={() => window.open(`tel:${data.phoneNumber}`)}>
                    <FontAwesomeIcon icon={faPhone} id={styles.icon} />
                    <div>
                        <h1>{data.phoneNumber}</h1>
                        <h2>Click to call</h2>
                    </div>
                </div>

                <div style={{marginBottom: '12px', display: data.remotePosition ? 'none' : 'flex'}} id={styles.rentalPropertyLocation} onClick={() => { if(!data.remotePosition) { navigator.clipboard.writeText(`${data.propertyAddress}, ${data.propertyCity}, TX ${data.zip}`); successMsg("Employer address copied to clipboard") } }}>
                    <FontAwesomeIcon icon={faLocationDot} id={styles.icon} />

                    { data.remotePosition && <div>
                        <h1>{data.zip}</h1>
                        <h2>Remote Position available in this area code.</h2>
                    </div> }

                    { !data.remotePosition && <div>
                        <h1>{data.propertyAddress}, {data.propertyCity}, TX {data.zip}</h1>
                        <h2>Click to copy the property address</h2>
                    </div> }

                </div>

                <div style={{display: data.website.length === 0 ? 'none' : 'flex', marginBottom: '12px'}} id={styles.rentalPropertyLocation} onClick={() => window.open(data.website, '_blank')}>
                    <FontAwesomeIcon icon={faLink} id={styles.icon} />
                    <div>
                        <h1>Apply on Company Website</h1>
                        <h2>{data.website}</h2>
                    </div>
                </div>

                <hr />

                <section id={styles.posterProfile}>
                    <div>
                        <span style={{display: profileLoaded === false ? 'flex' : 'none'}}> <PuffLoader color='#154734' size={50} /> </span>
                        <Image sizes='auto auto' fill src={posterProfile.profilePicture === '' ? '/user.jpg' : posterProfile.profilePicture} alt={posterProfile.profilePicture === '' ? '/user.jpg' : posterProfile.profilePicture} onLoadingComplete={() => setProfileLoaded(true)} />
                    </div>
                    <div>
                        <h1>{posterProfile.displayName}</h1>
                        { reviewData > 0 && <section>
                            <FontAwesomeIcon style={{marginRight: '4px'}} icon={faStar} color={reviewData >= 1 ? '#FFB81C' : 'lightgrey'} />
                            <FontAwesomeIcon style={{marginRight: '4px'}} icon={faStar} color={reviewData >= 2 ? '#FFB81C' : 'lightgrey'} />
                            <FontAwesomeIcon style={{marginRight: '4px'}} icon={faStar} color={reviewData >= 3 ? '#FFB81C' : 'lightgrey'} />
                            <FontAwesomeIcon style={{marginRight: '4px'}} icon={faStar} color={reviewData >= 4 ? '#FFB81C' : 'lightgrey'} />
                            <FontAwesomeIcon icon={faStar} color={reviewData === 5 ? '#FFB81C' : 'lightgrey'} />
                        </section> }
                    </div>
                    <div>
                        <button onClick={() => openUserProfile(true)}>
                            <FontAwesomeIcon icon={faEye} id={styles.view} />
                        </button>
                    </div>
                </section>
                
                <section style={{display: authenticationIsOwnerCheck() ? 'flex' : 'none' }} id={styles.quickMessage}>
                    { !hasConvo || loadingHasConvo ?
                    <span style={{display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%'}}><BarLoader width={200} height={5} color='white' /></span>
                    :
                    <>
                        <div>
                            <FontAwesomeIcon icon={faMessage} id={styles.icon} />
                            <h1>{ !hasConvo.hasConvo ? 'Quick Message' : 'View Conversation' }</h1>
                        </div>
                        <div>
                            { !hasConvo.hasConvo && <textarea ref={quickMessageTextRef} placeholder="Write this user a message..." /> }
                            <FontAwesomeIcon onClick={!hasConvo.hasConvo ? handleQuickMessageSend : handleOpenCurrentConversation} icon={!hasConvo.hasConvo ? faPaperPlane : faEye} id={styles.icon} />
                        </div>
                    </>
                    }
                </section>

                { !session ?
                    <section id={styles.easyApply}>
                    <section>
                        <div>
                            <FontAwesomeIcon icon={faBoltLightning} id={styles.icon} />
                            <h1>Easy Apply</h1>
                        </div>
                        <div> <h1>Login To Apply</h1> </div>
                    </section>
                    </section>
                    :
                    <section style={{display: authenticationIsOwnerCheck() ? 'flex' : 'none' }} id={styles.easyApply}>
                    <section>
                        <div>
                            <FontAwesomeIcon icon={faBoltLightning} id={styles.icon} />
                            <h1>Easy Apply</h1>
                        </div>
                        { hasApplication ? <div> <h1>Application Received</h1> </div> :
                        <div style={{display: !hasApplication ? 'flex' : 'none'}}>
                            <input disabled={applyPending} ref={nameRef} type='text' placeholder="First & Last" />
                            <input disabled={applyPending} ref={phoneRef} onChange={handlePhoneInput} minLength={14} maxLength={14} type='text' placeholder='Phone#' value={phoneInput} />
                            <input disabled={applyPending} ref={emailRef} type='email' placeholder='Email Address' />
                            <section>
                                <div>
                                    { uploadingResume && <PuffLoader size={48} color='#818181' /> }
                                    { resumeData && !uploadingResume ? <FontAwesomeIcon icon={faFile} id={styles.icon} /> : 'Upload Resume' }
                                    { resumeData && !uploadingResume && !applyPending && <FontAwesomeIcon onClick={removeLogo} icon={faXmark} id={styles.remove} /> }
                                    { !resumeData && !uploadingResume && <input disabled={applyPending} type='file' accept="application/pdf, application/vnd.ms-excel" onChange={handleUploadResume} /> }
                                </div>
                                <div>
                                    <button onClick={handleEasyApply}>
                                        { applyPending ? <PuffLoader size={30} color='purple' /> : <FontAwesomeIcon icon={faPaperPlane} id={styles.icon} /> }
                                    </button>
                                </div>
                            </section>
                        </div>
                        }
                    </section>
                    </section>
                }

                <AdvertisementSpot adID={'6595648'} session={session} router={router} closeListing={closeListing} />
            </div>

        </div>
        }

    </div>
  )
}

const CreateReportBox = ({ listingID, reportingUser, closeReport, successfulReport }) => {

    const { data: session, status } = useSession();

    const [pendingReport, setPendingReport] = useState(false);

    const reportTextRef = useRef(null);
    const [reportReason, setReportReason] = useState(null);

    const generateReport = async () => {
        if(listingID === null) return;
        if(pendingReport === true) return;
        if(!session) return;
        if(status !== 'authenticated') return;
        if(reportReason === null) return errorMsg("Please select a report reason.")
        if(reportTextRef === null || reportTextRef.current.value.length === 0) return errorMsg("Please enter some details about your report.");
        if(reportTextRef.current.value.length > 500) return errorMsg("Please enter some details about your report.");

        const reasonText = reportTextRef.current.value;

        setPendingReport(true);

        const result = await axios.post(`${window.location.origin}/api/TFSSystem/createReport`, {
            requestingUserID: session.user.token.id,
            origin: 'Jobs',
            reportedUser: reportingUser,
            reasonType: reportReason,
            message: reasonText,
            listingID: listingID,
            creationDate: new Date().toISOString()
          }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString());
          })
      
    
        if(result !== undefined) {
            if(result.status === 200) {
                closeReport();
                successfulReport();
                successMsg("Listing has been reported, thank you.");
            } else {
                errorMsg("We ran into an issue, please try again later.");
            }
        }
        setPendingReport(false);
    }

    return (
        <motion.section initial={{x: listingID === null ? '-320px' : '0'}} animate={{x: listingID === null ? '-320px' : '0', transition: { bounce: '0' }}} id={styles.reportBox}>
            <FontAwesomeIcon onClick={closeReport} icon={faXmark} id={styles.exit} />
            <h1>Create Report</h1>

            <div id={styles.types}>
                <button onClick={() => setReportReason("Inappropriate Content")} disabled={pendingReport} style={reportReason === "Inappropriate Content" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Inappropriate Content</button>
                <button onClick={() => setReportReason("Spam or Scams")} disabled={pendingReport} style={reportReason === "Spam or Scams" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Spam or Scams</button>
                <button onClick={() => setReportReason("Safety Concerns")} disabled={pendingReport} style={reportReason === "Safety Concerns" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Safety Concerns</button>
                <button onClick={() => setReportReason("Illegal Activity")} disabled={pendingReport} style={reportReason === "Illegal Activity" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Illegal Activity</button>
                <button onClick={() => setReportReason("Privacy Violation")} disabled={pendingReport} style={reportReason === "Privacy Violation" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Privacy Violation</button>
                <button onClick={() => setReportReason("Offensive Behavior")} disabled={pendingReport} style={reportReason === "Offensive Behavior" ? { color: '#292929', backgroundColor: "#FFFFFF" } : null}>Offensive Behavior</button>
            </div>

            <textarea maxLength={500} disabled={pendingReport} ref={reportTextRef} style={{display: reportReason !== null ? 'flex' : 'none'}} placeholder='Please give us some detail...' />
            <button disabled={pendingReport} onClick={generateReport} style={{display: reportReason !== null ? 'flex' : 'none'}}>
                {pendingReport ? <BarLoader color='white' /> : 'Submit Report'}
            </button>
        </motion.section>
    )
}

const OptionBar = ({ listing, session, closeListing, authenticationIsOwnerCheck, handleCreateReport, reported }) => {

    const [saved, setSaved] = useState(listing?.saves?.some(saver => session?.user?.token?.username === saver));
    const [savePending, setSavePending] = useState(false);

    async function apiHandleSave() {
        if(savePending === true) return;
        setSavePending(true);

        const username = session.user.token.username;
        const listingID = listing._id;

        if(saved === false) {
          const result = await axios.post(`${window.location.origin}/api/jobs/helpers/save`, {
              listingID: listingID,
              username: username,
          }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString());
          })

          if(result && result.status === 200) {
            successMsg("Job has been saved");
            setSaved(true);
          }
          
        }
  
        if(saved === true) {
          const result = await axios.post(`${window.location.origin}/api/jobs/helpers/unsave`, {
              listingID: listingID,
              username: username,
          }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString());
          })
      
          if(result && result.status === 200) {
            successMsg("Job has been unsaved");
            setSaved(false);
          }
        }

        setSavePending(false)
    }

    return (
        <span id={styles.optionBar}>
            <span>
                { authenticationIsOwnerCheck() && <button id={styles.reportButton} onClick={() => reported === false && handleCreateReport()}> <FontAwesomeIcon icon={faFlag} id={styles.icon} color={reported === true ? '#FFB81C' : 'white'} /> </button> }
                { authenticationIsOwnerCheck() && <button id={styles.saveButton} onClick={() => apiHandleSave()}>

                { savePending === false && <FontAwesomeIcon icon={faBookmark} id={styles.icon} color={saved === true ? '#FFB81C' : 'white'} /> }
                { savePending === true && <PuffLoader size={28} color='white' /> }

                </button> }
                <button id={styles.shareButton} onClick={() => {navigator.clipboard.writeText(`${window.location.origin}/marketplace/jobs/?popup=sharedPost&_id=${listing._id}`); successMsg("Link Copied") } } > <FontAwesomeIcon icon={faShare} id={styles.icon} /> </button>
                <button id={styles.exitButton} onClick={() => closeListing(null)} > <FontAwesomeIcon icon={faXmarkCircle} id={styles.icon} /> </button>
            </span>
        </span>
    )
}

const AdvertisementSpot = ({ adID, session, router, closeListing }) => {

    const adContainerRef = useRef(null);
  
    const adInView = useInView(adContainerRef, { once: true })
    const [adShown, setAdShown] = useState(false);
    const [adData, setAdData] = useState({ img: null, url: null });
  
    useEffect(() => {
      if(adInView === false) return;
      if(adShown === true) return;
      
      async function getAdImage() {
        setAdShown(true); // Ad was in view port
        setAdData({ img: true, url: null }); // Loading State
  
        const retrieveAdImg = await axios.post(`${window.location.origin}/api/admanager/retrieveAd`, {
          adID: adID
        }).catch((error) => {
          if(error?.response?.data?.status !== undefined) {
            console.error(error?.response?.data?.status);
          } else {
            console.error(error.toString());
          }
        })
  
        if(retrieveAdImg !== undefined) {
          if(retrieveAdImg.status === 200) {
            const img = retrieveAdImg.data.image; //img link
            const url = retrieveAdImg.data.url; //url link
            
            const structure = {
              img: img === 'NO_IMAGE' ? false : img,
              url: url
            }
  
            setAdData(structure);
          } else {
            console.error("There was an error when loading the advertisement image.");
          }
        }
      }
  
      getAdImage();
  
    }, [adInView])
  
    async function handleClick(e) {
      e.preventDefault();
      window.open(adData.url, '_blank');
  
      await axios.post(`${window.location.origin}/api/admanager/countClick`, {
        adID: adID
      }).catch((error) => {
        console.error(error.toString());
      })
    }
  
    const [coverLoaded, setCoverLoaded] = useState(false);
  
    return (
      <section ref={adContainerRef} id={styles.adSpot} style={{cursor: 'pointer', borderWidth: adData.img !== null && adData.img !== true & adData.img !== false && adData.img !== "NO_IMAGE" ? 0 : null}}>
        { adData.img === null && <h1>Ad Spot Available</h1> }
        { adData.img === false && <h1>Ad Spot Available</h1> }
        { adData.img === true && <RiseLoader size={15} color='#154734' /> }
        { (adData.img === null || adData.img === false) && session !== null && session !== undefined && <button onClick={() => { openAdManager(router); closeListing(null)} }><FontAwesomeIcon icon={faAd} id={styles.icon} /> Manager</button>}
  
        { adData.img !== null && adData.img !== true && adData.img !== false && <>
          <h6>Sponsored Advertisement</h6>
          <span style={{width: '100%', height: '100%', display: coverLoaded === true ? 'none' : 'flex', justifyContent: 'center', alignItems: 'center'}}>
            <RiseLoader size={15} color='#154734' />
          </span>
          <Image onClick={(e) => handleClick(e)} sizes='auto auto' style={{objectFit: 'contain'}} fill src={adData.img} alt={adData.img} onLoadingComplete={() => setCoverLoaded(true)} />
        </> }
      </section>
    )
  }

/* Additional Functions */
/* ==================== */

function uppercaseFirst(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

function returnRating(reviews) {
    let rating;
    let totalStars = 0;
    if(reviews?.length === 0) {
        rating = 0;
    } else {
        reviews?.forEach((review) => {
            totalStars+= review?.rating;
        })
        rating = totalStars / reviews?.length;
    }
    rating = Math.ceil(parseFloat(rating.toFixed(2)));

    return rating;
}

function createdTimeStamp(creationDate) {
    let displayTime;
    let displayTimeType;

    var delta = Math.floor(new Date().getTime() - new Date(creationDate).getTime());

    var seconds = Math.floor(delta / 1000);
    var minutes = Math.floor(seconds / 60);
    var hours =  Math.floor(minutes / 60);
    var days = Math.floor(hours / 24);
    var weeks = Math.floor(days / 7);
    var months = Math.floor(weeks / 4);

    if(seconds <= 10) {
        displayTime = "just now";
        displayTimeType = ""
    } else {
        displayTime = seconds;
        displayTimeType = "seconds ago"
    }

    if(minutes > 1) {
        displayTime = minutes;
        displayTimeType = 'minutes ago'
    }

    if(minutes === 1) {
        displayTime = minutes;
        displayTimeType = 'minute ago'
    }

    if(hours > 1) {
        displayTime = hours;
        displayTimeType = 'hours ago'
    }

    if(hours === 1) {
        displayTime = hours;
        displayTimeType = 'hour ago'
    }
    
    if(days > 1) {
        displayTime = days;
        displayTimeType = 'days ago'
    }

    if(days === 1) {
        displayTime = days;
        displayTimeType = 'day ago'
    }

    if(weeks > 1) {
        displayTime = weeks;
        displayTimeType = 'weeks ago'
    }

    if(weeks === 1) {
        displayTime = weeks;
        displayTimeType = 'week ago'
    }

    if(months > 1) {
        displayTime = months;
        displayTimeType = 'months ago'
    }

    if(months === 1) {
        displayTime = months;
        displayTimeType = 'month ago'
    }

    return {displayTime, displayTimeType}
}